import {
  type BigDecimal,
  absoluteValue,
  createBigDecimal,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type {
  Action,
  CryptoCurrency,
  FiatCurrency,
  Transfer,
} from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { sodexAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { fetchSpotSymbolMap } from '../fetchSymbolMap'
import { normalizeUserAddress } from '../normalizeUserAddress'
import { resolveCoinAsset } from '../resolveCoinAsset'
import { responseSchema } from './schema'

/**
 * 現物約定履歴 API から Action 一覧を取得する
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })
  const userAddress = normalizeUserAddress({ address: apiKey })

  const client = createClient({
    market: 'spot',
    createHeaders,
    proxy,
  })

  const symbolMap = await fetchSpotSymbolMap({ client })

  const query: Record<string, string> = {
    limit: config.limit.toString(),
    ...(from ? { startTime: from.toString() } : {}),
    ...(to ? { endTime: to.toString() } : {}),
  }

  const trades =
    (await client.run({
      method: 'GET',
      path: `/accounts/${userAddress}/trades`,
      query,
      responseSchema,
    })) ?? []

  const sortedTrades = trades.toSorted((a, b) => a.time - b.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  const tradeGroups = new Map<string, typeof sortedTrades>()
  for (const trade of sortedTrades) {
    fetchedFrom =
      fetchedFrom === undefined ? trade.time : Math.min(fetchedFrom, trade.time)
    fetchedTo =
      fetchedTo === undefined ? trade.time : Math.max(fetchedTo, trade.time)

    const key = `${trade.orderID}_${trade.time}`
    const group = tradeGroups.get(key) ?? []
    group.push(trade)
    tradeGroups.set(key, group)
  }

  for (const group of tradeGroups.values()) {
    const firstTrade = group[0]
    const pair = symbolMap.get(firstTrade.symbol)
    if (!pair) {
      throw new AssetNotFoundException(firstTrade.symbol)
    }

    const baseAsset = resolveCoinAsset({ coin: pair.base, getAsset })
    const quoteAsset = resolveCoinAsset({ coin: pair.quote, getAsset })
    if (!baseAsset) {
      throw new AssetNotFoundException(pair.base)
    }
    if (!quoteAsset) {
      throw new AssetNotFoundException(pair.quote)
    }

    const changes = new Map<string, BigDecimal>()
    const assetById = new Map<string, CryptoCurrency | FiatCurrency>([
      [baseAsset.id, baseAsset],
      [quoteAsset.id, quoteAsset],
    ])

    for (const trade of group) {
      if (trade.side === 'BUY') {
        const baseAmount = changes.get(baseAsset.id) ?? createBigDecimal(0n)
        changes.set(baseAsset.id, plus(baseAmount, trade.quantity))
        const quoteAmount = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
        changes.set(
          quoteAsset.id,
          minus(quoteAmount, times(trade.quantity, trade.price)),
        )
      } else {
        const baseAmount = changes.get(baseAsset.id) ?? createBigDecimal(0n)
        changes.set(baseAsset.id, minus(baseAmount, trade.quantity))
        const quoteAmount = changes.get(quoteAsset.id) ?? createBigDecimal(0n)
        changes.set(
          quoteAsset.id,
          plus(quoteAmount, times(trade.quantity, trade.price)),
        )
      }

      if (trade.fee && trade.feeCoin) {
        const feeAsset = resolveCoinAsset({ coin: trade.feeCoin, getAsset })
        if (!feeAsset) {
          throw new AssetNotFoundException(trade.feeCoin)
        }
        assetById.set(feeAsset.id, feeAsset)
        const feeAmount = changes.get(feeAsset.id) ?? createBigDecimal(0n)
        changes.set(feeAsset.id, minus(feeAmount, trade.fee))
      }
    }

    const transfers: Transfer[] = []
    for (const [assetId, amount] of changes.entries()) {
      if (amount.value === 0n) {
        continue
      }
      const asset = assetById.get(assetId)
      if (!asset) {
        throw new AssetNotFoundException(assetId)
      }
      transfers.push({
        asset,
        amount: absoluteValue(amount),
        direction: amount.value > 0n ? 'in' : 'out',
      })
    }

    actions.push({
      comment: `Trade ID: ${group.map((t) => t.tradeID).join(' / ')}`,
      evidence: 'system-rule',
      order: 0,
      source: `${sodexAccountCategory}_${config.type}_${firstTrade.orderID}_${firstTrade.time}`,
      type: 'trade',
      timestamp: firstTrade.time,
      transfers,
    })
  }

  const sortedActions = actions.toSorted((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: trades.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
